// src/hooks/useSaveToPMS.ts

import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { endpoints } from '@/react-query/endpoints';
import { useSubmitWriteback } from '@/react-query/videa-notes/useSubmitWriteback';
import { useUpdateNotes } from '@/react-query/videa-notes/useUpdateNotes';

import { getNoteSummaryWithoutBlurb } from './useNoteManagement';

interface UseSaveNoteToPMSProps {
  currentOrganizationId: string;
  currentPracticeId: string;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  selectedNoteId: string | undefined;
  noteContent: string;
  hasWritebackEnabled: boolean;
}

export const useSaveNoteToPMS = ({
  currentOrganizationId,
  currentPracticeId,
  unifiedAppointmentId,
  standalonePatientVisitId,
  selectedNoteId,
  noteContent,
  hasWritebackEnabled,
}: UseSaveNoteToPMSProps) => {
  const queryClient = useQueryClient();

  const updateNoteMutation = useUpdateNotes();
  const submitWritebackMutation = useSubmitWriteback();

  const triggerSaveNoteToPMS = useCallback(() => {
    if (!selectedNoteId) return;

    const updatedSummary = getNoteSummaryWithoutBlurb(
      noteContent,
      hasWritebackEnabled,
    );

    updateNoteMutation.mutate(
      {
        clinicalNoteId: selectedNoteId,
        updateNotesBody: {
          editedSummary: updatedSummary,
        },
      },
      {
        onSuccess: () => {
          submitWritebackMutation.mutate(selectedNoteId, {
            onSuccess: () => {
              // Invalidate the note cache for the specific appointment
              if (unifiedAppointmentId) {
                queryClient.invalidateQueries({
                  queryKey: [
                    'videa-notes',
                    currentOrganizationId,
                    currentPracticeId,
                    unifiedAppointmentId,
                  ],
                });
              }
              if (standalonePatientVisitId) {
                queryClient.invalidateQueries({
                  queryKey: [
                    'videa-notes',
                    currentOrganizationId,
                    currentPracticeId,
                    standalonePatientVisitId,
                  ],
                });
              }
              // Invalidate the note cache for all appointments to ensure correct status is displayed across Ambient table
              queryClient.invalidateQueries([
                endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
              ]);
              queryClient.invalidateQueries([
                endpoints.ambient.voiceNotes
                  .batchNotesByStandalonePatientVisitIds,
              ]);
            },
          });
        },
      },
    );
  }, [
    selectedNoteId,
    noteContent,
    hasWritebackEnabled,
    updateNoteMutation,
    submitWritebackMutation,
    queryClient,
    currentOrganizationId,
    currentPracticeId,
    unifiedAppointmentId,
    standalonePatientVisitId,
  ]);

  return {
    triggerSaveNoteToPMS,
    isSavingNoteToPMS:
      updateNoteMutation.isLoading || submitWritebackMutation.isLoading,
  };
};
